import { Box, Typography, useTheme } from "@mui/material";
import { FC, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import CustomButton from "../../components/CustomButton";
import { Routes } from "../../navigation/navigation-types";
import { WebsiteManager } from "../../services/api/WebsiteManager";
import { WebsiteDto } from "../../shared/types/websiteTypes";
import { HeurekaApiKeyModal } from "./components/Modals/HeurekaApiKeyModal";
import { ZboziApiKeyModal } from "./components/Modals/ZboziApiKeyModal";
import DoneIcon from "@mui/icons-material/Done";
import { showFlashMessageWithTimeout } from "../../services/actions/flashMessage-actions";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { useStyles } from "./AddApiKeys.style";
import { ApiKeysList } from "./components/ApiKeysList";
import { ServicesType } from "./components/types";
import { API_CONFIG } from "./components/constants";
import SpolehliveRecenzeLoginModal from "./components/Modals/SpolehliveRecenzeLoginModal";
import { GoogleReviewApiKeyModal } from "./components/Modals/GoogleReviewApiKeyModal";
import { TrustPilotApiKeyModal } from "./components/Modals/TrustPilotApiKeyModal";
import { isUSA } from "../../constants/env";

interface AddApiKeysProps {
  goToNext?: () => void;
  goToPrevious?: () => void;
  createdWebId: string;
  // Includes different styling for connection screen
  isConnectionsScreen?: boolean;
}

const AddApiKeys: FC<AddApiKeysProps> = ({
  goToNext,
  goToPrevious,
  createdWebId,
  isConnectionsScreen,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [heurekaModalOpen, setHeurekaModalOpen] = useState<boolean>(false);
  const [zboziModalOpen, setZboziModalOpen] = useState<boolean>(false);
  const [googleReviewsModalOpen, setGoogleReviewsModalOpen] =
    useState<boolean>(false);
  const [spolehliveRecenzeModalOpen, setSpolehliveRecenzeModalOpen] =
    useState<boolean>(false);
  const [trustPilotModalOpen, setTrustPilotModalOpen] =
    useState<boolean>(false);
  const [websiteData, setWebsiteData] = useState<WebsiteDto | undefined>();
  const [isWebsiteLoading, setIsWebsiteLoading] = useState<boolean>(false);

  useEffect(() => {
    const fetchSiteData = async () => {
      setIsWebsiteLoading(true);
      try {
        const _websiteManager = WebsiteManager.getManager();
        const result: WebsiteDto = await _websiteManager.getWebsiteSingleAsync(
          createdWebId,
        );
        setWebsiteData(result);
        setIsWebsiteLoading(false);
      } catch (e: any) {
        setIsWebsiteLoading(false);
        showFlashMessageWithTimeout(dispatch, e.toString(), "error");
      }
    };
    if (createdWebId) {
      fetchSiteData();
    }
  }, [
    heurekaModalOpen,
    zboziModalOpen,
    spolehliveRecenzeModalOpen,
    googleReviewsModalOpen,
    createdWebId,
    dispatch,
  ]);

  const getStringStatus = (
    type: ServicesType,
    websiteData?: WebsiteDto,
  ): string => {
    const statusMap: { [key in ServicesType]: string | undefined } = {
      heureka: websiteData?.heurekaApiKey,
      zbozi: websiteData?.zboziApiKey,
      spolehliveRecenze: websiteData?.spolehlivaRecenzeShopId,
      googleReviews: websiteData?.googleReviewsPlaceId,
      trustPilot: websiteData?.trustpilotBusinessUnitId,
    };

    return statusMap[type] ? t("popups.added") : t("add");
  };

  const statusSuccess = (
    <Box className={classes.statusContainer}>
      <Box className={classes.iconContainer}>
        <DoneIcon style={{ color: "#FBFAFC" }} />
      </Box>
      <Typography className={classes.statusTitle}>{t("connected")}</Typography>
    </Box>
  );

  return (
    <Box
      className={classes.container}
      style={{ justifyContent: isConnectionsScreen ? "flex-start" : "center" }}
    >
      <Box
        className={classes.formContainer}
        style={{
          alignItems: isConnectionsScreen ? "stretch" : "center",
          marginTop: isConnectionsScreen ? 24 : 65,
        }}
      >
        <Typography
          className={classes.title}
          style={{ marginBottom: isConnectionsScreen ? 0 : 20 }}
        >
          {isConnectionsScreen ? t("apiKeysTitle") : t("addingApiKeys")}
        </Typography>
        {isConnectionsScreen && !isUSA && new Date() < new Date("2026-05-30") && (
          <Box
            style={{
              backgroundColor: "#FFF3CD",
              border: "1px solid #FFCA2C",
              borderRadius: 8,
              padding: "12px 16px",
              marginTop: 12,
              marginBottom: 4,
            }}
          >
            <Typography style={{ fontSize: 14, color: "#664D03" }}>
              {t("zboziApiChangeNotice")}
            </Typography>
          </Box>
        )}
        {!isConnectionsScreen && (
          <Typography style={{ fontSize: 16, color: theme.palette.natural.t1 }}>
            {t("optionalAddApiKey")}
          </Typography>
        )}

        {websiteData && (
          <ApiKeysList
            apiKeys={Object.values(API_CONFIG)}
            websiteData={websiteData}
            isWebsiteLoading={isWebsiteLoading}
            setHeurekaModalOpen={setHeurekaModalOpen}
            setZboziModalOpen={setZboziModalOpen}
            setSpolehliveRecenzeModalOpen={setSpolehliveRecenzeModalOpen}
            setGoogleReviewsOpen={setGoogleReviewsModalOpen}
            setTrustPilotModalOpen={setTrustPilotModalOpen}
            statusSuccess={statusSuccess}
            getStringStatus={getStringStatus}
            t={t}
            classes={classes}
          />
        )}

        {!isConnectionsScreen && (
          <Box className={classes.buttonContainer}>
            <CustomButton
              isLoading={false}
              title={t("continue")}
              onClick={() => navigate(Routes.campaign)}
              style={{ width: 120, marginLeft: 20 }}
            />
            <CustomButton
              title={t("back")}
              type='secondary'
              onClick={goToPrevious || (() => {})}
              size='large'
              style={{ width: 100 }}
            />
          </Box>
        )}
      </Box>

      {/* Modals */}
      <HeurekaApiKeyModal
        websiteData={websiteData}
        createdWebId={createdWebId}
        isOpen={heurekaModalOpen}
        onClose={() => setHeurekaModalOpen(false)}
      />
      <ZboziApiKeyModal
        websiteData={websiteData}
        createdWebId={createdWebId}
        isOpen={zboziModalOpen}
        onClose={() => setZboziModalOpen(false)}
      />
      <SpolehliveRecenzeLoginModal
        websiteData={websiteData}
        createdWebId={createdWebId}
        isOpen={spolehliveRecenzeModalOpen}
        onClose={() => setSpolehliveRecenzeModalOpen(false)}
      />
      <GoogleReviewApiKeyModal
        websiteData={websiteData}
        createdWebId={createdWebId}
        isOpen={googleReviewsModalOpen}
        onClose={() => setGoogleReviewsModalOpen(false)}
      />
      <TrustPilotApiKeyModal
        websiteData={websiteData}
        createdWebId={createdWebId}
        isOpen={trustPilotModalOpen}
        onClose={() => setTrustPilotModalOpen(false)}
      />
    </Box>
  );
};

export default AddApiKeys;
